/** @format */

import { ButtonGroup, Flex, FormControl, SimpleGrid } from "@chakra-ui/react";
import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import Card from "../../../components/card/Card";
import CustomButton from "../../../components/Common/CustomButton";
import CustomInput from "../../../components/Common/CustomInput";
import CustomSelect from "../../../components/Common/CustomInput/CustomSelect";
import CustomSunEditor from "../../../components/Common/CustomInput/suneditor";
import IsLoading from "../../../components/Common/IsLoading";
import { StatusData } from "../Category/Create";
import { actions as groupActions } from "../group/redux/slice";
import FileUploadBox from "../product/FileUploadBox";
import { ProductType } from "../product/types";
import { selectAllRetailerCategoryList } from "../retailer-category/redux/selector";
import { actions as categoryActions } from "../retailer-category/redux/slice";
import {
  selectButtonLoading,
  selectFormData,
  selectLoading,
} from "./redux/selector";
import { actions } from "./redux/slice";

const Create = () => {
  const { t } = useTranslation();
  const params = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const form = useSelector(selectFormData);
  const categoryList = useSelector(selectAllRetailerCategoryList);
  const loader = useSelector(selectLoading);
  const loading = useSelector(selectButtonLoading);

  React.useEffect(() => {
    dispatch(groupActions.doGetAllGroupList());
    dispatch(categoryActions.doGetAllCategoryList());
    return () => {};
  }, []);
  React.useEffect(() => {
    if (params.id) {
      dispatch(actions.doGetById(params?.id));
    }
    return () => {};
  }, [params.id]);

  const handleFieldChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = evt.target;
    dispatch(actions.updateFormValue({ key: name, value: value }));
  };
  const handleChangeEditor = (name: string, value: any) => {
    dispatch(actions.updateFormValue({ key: name, value: value }));
  };
  const handleCancel = () => {
    dispatch(actions.clearFrom());
    navigate(-1);
  };

  const handleChangeUploadField = (name: string, val: any) => {
    dispatch(actions.updateImagesFormValue({ name: name, value: val }));
  };
  const handleChangeFeaturedImage = (val: string) => {
    dispatch(actions.updateFeaturedImageFormValue(val));
  };
  const handleDeleteFile = (name: string, index: number) => {
    dispatch(actions.doRemoveImages({ name: name, index: index }));
  };

  const handleSubmit = () => {
    if (form?._id) {
      dispatch(
        actions.doUpdate({
          callback() {
            dispatch(actions.clearFrom());
            navigate(-1);
          },
        })
      );
    } else {
      dispatch(
        actions.doCreate({
          callback() {
            dispatch(actions.clearFrom());
            navigate(-1);
          },
        })
      );
    }
  };

  return (
    <Flex pt={{ base: "130px", md: "80px", xl: "80px" }}>
      <Card>
        {loader ? (
          <IsLoading />
        ) : (
          <FormControl>
            <SimpleGrid columns={[1, 1, 2, 3]} spacingX={5}>
              <CustomInput
                name={"title"}
                value={form?.title}
                handleChange={handleFieldChange}
                placeholder={`${t("product.form.titleCode")}`}
                title={`${t("product.form.title")}`}
              />
              <CustomInput
                name={"code"}
                value={form?.code}
                handleChange={handleFieldChange}
                placeholder={`${t("product.form.enterCode")}`}
                title={`${t("product.form.code")}`}
              />
              <CustomInput
                name={"price"}
                value={form?.price}
                handleChange={handleFieldChange}
                placeholder={`${t("product.form.enterPrice")}`}
                title={`${t("product.form.price")}`}
                type='number'
              />
              <CustomInput
                name={"sellingPrice"}
                value={form?.sellingPrice}
                handleChange={handleFieldChange}
                placeholder={`${t("product.form.enterSellingPrice")}`}
                title={`${t("product.form.sellingPrice")}`}
                type='number'
              />

              <CustomInput
                name={"taxRate"}
                value={form?.taxRate}
                handleChange={handleFieldChange}
                placeholder={`${t("product.form.enterTaxRate")}`}
                title={`${t("product.form.taxRate")}`}
                type='number'
              />
              <CustomInput
                name={"shipmentCapacity"}
                value={form?.shipmentCapacity}
                handleChange={handleFieldChange}
                placeholder={`${t("product.form.enterShipmentCapacity")}`}
                title={`${t("product.form.shipmentCapacity")}`}
                type='number'
              />

              <CustomSelect
                name={"categoryId"}
                value={form?.categoryId}
                handleChange={handleFieldChange}
                title={`${t("product.form.category")}`}
                data={categoryList}
                isFromApi
              />

              <CustomSelect
                name={"status"}
                value={form?.status}
                handleChange={handleFieldChange}
                title={`${t("common.form.status")}`}
                data={StatusData}
              />
              <CustomInput
                name={"size"}
                value={form?.size}
                handleChange={handleFieldChange}
                placeholder={`${t("product.form.enterSize")}`}
                title={`${t("product.form.size")}`}
                isTextArea
                rows={5}
              />
              <CustomInput
                name={"shortDescription"}
                value={form?.shortDescription}
                handleChange={handleFieldChange}
                placeholder={`${t("product.form.enterShortDescription")}`}
                title={`${t("product.form.shortDescription")}`}
                isTextArea
                rows={5}
              />
              <CustomSunEditor
                name='longDescription'
                value={form.longDescription}
                handleChange={handleChangeEditor}
                placeholder={`${t("product.form.enterLongDescription")}`}
                title={`${t("product.form.longDescription")}`}
              />
            </SimpleGrid>
            <FileUploadBox
              handleChangeFeaturedImage={handleChangeFeaturedImage}
              handleChangeUploadField={handleChangeUploadField}
              handleDeleteFile={handleDeleteFile}
              form={form}
            />
            <SimpleGrid columns={1} spacing={10}>
              <Flex flexDirection='column'>
                <ButtonGroup justifyContent={"flex-end"} mt={8}>
                  <CustomButton
                    handleClick={handleCancel}
                    title={t("common.button.cancel")}
                    loading={false}
                    variant='light'
                    sx={{ marginRight: "20px" }}
                  />
                  <CustomButton
                    handleClick={handleSubmit}
                    title={t("common.button.save")}
                    loading={loading}
                    disabled={!form?.title}
                    sx={{ marginLeft: "20px" }}
                  />
                </ButtonGroup>
              </Flex>
            </SimpleGrid>
          </FormControl>
        )}
      </Card>
    </Flex>
  );
};

export default Create;
export const TypeData = [
  { _id: ProductType.SINGLE, value: "product.form.individualItem" },
  { _id: ProductType.PACKAGE, value: "product.form.packageItem" },
];
